/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */
import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  memo,
  useMemo,
  useEffect
} from 'react'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardDatePickerProps
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import localeDe from 'date-fns/locale/de'
import TextField, { TextFieldProps } from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/styles'
import { useTheme } from '@material-ui/core/styles'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { InputLabel, useMediaQuery, Theme } from '@material-ui/core'
import Tooltip from 'components/Tooltip'
import TooltipButton from 'components/Tooltip/Button'
import { ParsableDate } from '@material-ui/pickers/constants/prop-types'
import { isTenantSmava } from 'utils/url'
import { scrollTo } from 'utils/pollard/scroll'
import { isInIframeContext } from 'stores/RootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { convertFormat } from 'components/PathDateInput/helpers'

interface OwnProps {
  onChange?: KeyboardDatePickerProps['onChange']
  onBlur?: (date: ParsableDate | null, raw?: string | null) => void
  onFocus?: (date: ParsableDate | null, raw?: string | null) => void
  value?: string | KeyboardDatePickerProps['value']
  onTooltip?: (isDisplayed: boolean) => void
  tooltip?: string
  showTooltip?: boolean
  error?: string
  path?: string
}

type DatePickerProps = Omit<
  KeyboardDatePickerProps,
  | 'helperText'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'value'
  | 'inputValue'
  | 'open'
  | 'error'
  | 'TextFieldComponent'
> &
  OwnProps

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  textFieldContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  },
  root: {
    margin: 0,
    '& .Mui-error': {
      padding: '0'
    }
  }
}))

export const PATHS_WITH_YEAR_AUTOCOMPLETION = [
  'debtors.primary.personal.birthday',
  'debtors.secondary.personal.birthday'
]

const DatePicker = forwardRef<HTMLDivElement | null, DatePickerProps>(
  (
    {
      value = null,
      onChange,
      onBlur,
      onFocus,
      tooltip,
      onTooltip,
      showTooltip = false,
      error,
      inputVariant = 'outlined',
      label,
      path,
      ...restProps
    }: DatePickerProps,
    ref
  ) => {
    const {
      navigation: { currentPageInSession }
    } = useStores()
    const { textFieldContainer, container, labelStyle, root } = useStyles()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const textRef = useRef<HTMLDivElement | null>(null)
    const labelText =
      textRef.current?.getElementsByTagName('label')?.[0]?.innerText

    const [shouldShrink, setShrink] = useState<boolean | undefined>(undefined)
    const [showHint, setHint] = useState<boolean>(false)

    const isSmava = isTenantSmava()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

    const [selectedDate, setSelectedDate] = useState(
      typeof value === 'string' && value.length === 0 ? null : value
    )
    const [text, setText] = useState(
      typeof value === 'string' ? value : value?.toString() || ''
    )

    const { format } = restProps

    const handleChange = useCallback(
      (date: MaterialUiPickersDate | null, raw?: string | null) => {
        setSelectedDate(date)
        setText(raw || '')

        setShrink(true)

        if (onChange) {
          onChange(date, raw)
        }
      },
      [onChange]
    )

    const handleBlur = useCallback(() => {
      setShrink(
        Boolean(selectedDate) ||
          Boolean(isSmallScreen && labelText && labelText.length > 10)
      )

      setTimeout(() => {
        setHint(false)
      }, 300)

      if (!onBlur) {
        return
      }

      if (path && PATHS_WITH_YEAR_AUTOCOMPLETION.includes(path) && format) {
        setText(convertFormat(text, format, format))
      }

      onBlur(selectedDate, text)
    }, [onBlur, selectedDate, text, isSmallScreen, labelText, format, path])

    useEffect(() => {
      if (isSmallScreen && labelText && labelText.length > 10) {
        setShrink(true)
      }
    }, [isSmallScreen, labelText])

    const handleFocus = useCallback(() => {
      setShrink(true)
      if (!onFocus) {
        return
      }

      onFocus(selectedDate, text)

      if (textRef.current && isSmava && isMobile) {
        const scrollTop =
          window.scrollY ||
          window.pageYOffset ||
          document.body.scrollTop +
            ((document.documentElement && document.documentElement.scrollTop) ||
              0)

        const pos = textRef.current.getBoundingClientRect().top + scrollTop
        scrollTo(pos - 200, isInIframeContext())
      }

      setTimeout(() => {
        setHint(isSmava && currentPageInSession !== 'contact')
      }, 300)
    }, [onFocus, selectedDate, text, isSmava, isMobile, currentPageInSession])

    const [isTooltipDisplayed, setTooltipDisplayed] = useState(showTooltip)
    const tooltipDisplayRef = useRef(isTooltipDisplayed)
    const toggleTooltip = useCallback(() => {
      tooltipDisplayRef.current = !tooltipDisplayRef.current
      setTooltipDisplayed(tooltipDisplayRef.current)

      if (onTooltip) {
        onTooltip(tooltipDisplayRef.current)
      }
    }, [onTooltip])

    const [isOpen, setOpen] = useState(false)
    const openPicker = useCallback(() => setOpen(true), [setOpen])
    const closePicker = useCallback(() => setOpen(false), [setOpen])

    const inputLabelProps = useMemo(
      () => ({
        shrink: shouldShrink
      }),
      [shouldShrink]
    )

    const inputId =
      label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

    const renderTextField = useCallback(
      ({ ...props }: TextFieldProps) => {
        return (
          <div className={container}>
            <div className={textFieldContainer}>
              <TextField
                size={isSmava ? 'small' : undefined}
                id={inputId || ''}
                ref={textRef}
                {...props}
                type="tel"
                inputMode="numeric"
                variant="outlined"
                InputProps={{ endAdornment: null }}
                className={root}
              />
              {tooltip && !isSmava && <TooltipButton onClick={toggleTooltip} />}
            </div>
          </div>
        )
      },
      [
        container,
        textFieldContainer,
        isSmava,
        inputId,
        root,
        tooltip,
        toggleTooltip
      ]
    )

    return (
      <>
        {isSmava && (
          <InputLabel htmlFor={inputId || ''} classes={{ root: labelStyle }}>
            {label}
          </InputLabel>
        )}
        {tooltip && <Tooltip open={showHint}>{tooltip}</Tooltip>}
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeDe}>
          <KeyboardDatePicker
            {...restProps}
            label={!isSmava && label}
            InputLabelProps={inputLabelProps}
            ref={ref}
            inputVariant={inputVariant}
            open={isOpen}
            onOpen={openPicker}
            onClose={closePicker}
            value={selectedDate}
            inputValue={text}
            helperText={error}
            error={!!error}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            fullWidth
            TextFieldComponent={renderTextField}
          />
        </MuiPickersUtilsProvider>
        {tooltip && !isSmava && (
          <Tooltip open={isTooltipDisplayed}>{tooltip}</Tooltip>
        )}
      </>
    )
  }
)

DatePicker.displayName = 'DatePicker'

export default memo(DatePicker)
